import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Users, Ruler, Calendar, CheckCircle, Star, MessageCircle } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { SEO } from "@/components/SEO";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ChateauEditorWithUpload } from "@/components/ChateauEditorWithUpload";
import { useAdminAuth } from "@/components/AdminAuth";
import { useChateauImages } from "@/hooks/useChateauImages";
import { useChateaux } from "@/hooks/useChateaux";
import { toast } from "@/hooks/use-toast";
import { ReservationForm } from "@/components/ReservationForm";
import { gtag } from "@/utils/gtag";

// Données des châteaux complètes (SANS IMAGES - utilise la base de données)
const chateauxData = {
  "disco-m": {
    nom: "Château Disco M",
    taille: "3,5m x 3,5m x 4m",
    capacite: "8 enfants",
    prix: "110€",
    description: "Château Disco avec musique pour une ambiance de fête extraordinaire.",
    caracteristiques: ["Musique intégrée", "Éclairage disco", "Zone de saut", "Design festif"],
    ageRecommande: "4-12 ans",
    inclus: ["Diable pour transport", "Bâche pour le sol", "Fiche explicative", "Piquets d'ancrage", "Soufflerie", "Système musical"],
    espaceRequis: "4,5m x 4,5m minimum",
    installation: "10 minutes avec aide explicative"
  },
  "grenouille": {
    nom: "Château Grenouille",
    taille: "5m x 3,5m x 4m",
    capacite: "8 enfants",
    prix: "90€",
    description: "Château Grenouille avec toboggan pour sauter comme de vraies grenouilles.",
    caracteristiques: ["Toboggan intégré", "Thème grenouille", "Zone de saut", "Couleurs vives"],
    ageRecommande: "3-12 ans",
    inclus: ["Diable pour transport", "Bâche pour le sol", "Fiche explicative", "Piquets d'ancrage", "Soufflerie"],
    espaceRequis: "5,5m x 4,5m minimum",
    installation: "10 minutes avec aide explicative"
  },
  "disco-lm": {
    nom: "Château Disco LM",
    taille: "4m x 4,5m x 4m",
    capacite: "8 enfants",
    prix: "120€",
    description: "Château Disco avec lumière disco et baffle pour une fête complète.",
    caracteristiques: ["Lumière disco", "Baffle intégré", "Musique", "Grande zone"],
    ageRecommande: "4-14 ans",
    inclus: ["Diable pour transport", "Bâche pour le sol", "Fiche explicative", "Piquets d'ancrage", "Soufflerie", "Système disco"],
    espaceRequis: "5m x 5,5m minimum",
    installation: "15 minutes avec aide explicative"
  },
  "twister": {
    nom: "Château Twister",
    taille: "3,5m x 3,5m x 4m",
    capacite: "8 enfants",
    prix: "60€",
    description: "Le château aux couleurs du célèbre jeu Twister pour encore plus de fun.",
    caracteristiques: ["Couleurs Twister", "Design unique", "Zone de saut", "Compact"],
    ageRecommande: "4-12 ans",
    inclus: ["Diable pour transport", "Bâche pour le sol", "Fiche explicative", "Piquets d'ancrage", "Soufflerie"],
    espaceRequis: "4,5m x 4,5m minimum",
    installation: "10 minutes avec aide explicative"
  },
  "dino": {
    nom: "Château Dino",
    taille: "3,5m x 3,5m x 4m",
    capacite: "8 enfants",
    prix: "80€",
    description: "Château Dino avec toboggan pour une aventure préhistorique.",
    caracteristiques: ["Toboggan intégré", "Thème dinosaure", "Décorations 3D", "Zone sécurisée"],
    ageRecommande: "3-10 ans",
    inclus: ["Diable pour transport", "Bâche pour le sol", "Fiche explicative", "Piquets d'ancrage", "Soufflerie"],
    espaceRequis: "4,5m x 4,5m minimum",
    installation: "10 minutes avec aide explicative"
  },
  "vache": {
    nom: "Château Vache",
    taille: "3,5m x 5m x 4m",
    capacite: "8 enfants",
    prix: "100€",
    description: "Château Vache avec toboggan pour une ambiance ferme amusante.",
    caracteristiques: ["Toboggan intégré", "Thème ferme", "Motifs vaches", "Grande surface"],
    ageRecommande: "3-12 ans",
    inclus: ["Diable pour transport", "Bâche pour le sol", "Fiche explicative", "Piquets d'ancrage", "Soufflerie"],
    espaceRequis: "4,5m x 6m minimum",
    installation: "10 minutes avec aide explicative"
  },
  "princesse": {
    nom: "Château Princesse",
    taille: "3,5m x 3,5m x 4m",
    capacite: "8 enfants",
    prix: "90€",
    description: "Château Princesse avec toboggan pour les rêves de toutes les petites filles.",
    caracteristiques: ["Toboggan intégré", "Couleurs rose/violet", "Tours de château", "Décorations princesse"],
    ageRecommande: "3-12 ans",
    inclus: ["Diable pour transport", "Bâche pour le sol", "Fiche explicative", "Piquets d'ancrage", "Soufflerie"],
    espaceRequis: "4,5m x 4,5m minimum",
    installation: "10 minutes avec aide explicative"
  },
  "pingouin": {
    nom: "Château Pingouin",
    taille: "3,5m x 5m x 4m",
    capacite: "8 enfants",
    prix: "100€",
    description: "Château Pingouin avec toboggan pour s'amuser dans l'univers polaire.",
    caracteristiques: ["Toboggan latéral", "Thème pingouin", "Zone polaire", "Filets de protection"],
    ageRecommande: "3-12 ans",
    inclus: ["Diable pour transport", "Bâche pour le sol", "Fiche explicative", "Piquets d'ancrage", "Soufflerie"],
    espaceRequis: "4,5m x 6m minimum",
    installation: "10 minutes avec aide explicative"
  }
};

export const ChateauDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { isAdminLoggedIn } = useAdminAuth();
  const { chateaux, updateChateau, deleteChateau, toggleVisibility } = useChateaux();
  const { imageUrls, isLoading } = useChateauImages(id || "");
  
  // Chercher le château dans la base de données d'abord, puis fallback sur les données hardcodées
  const dbChateau = chateaux.find(c => c.id === id);
  const fallbackChateau = id ? chateauxData[id as keyof typeof chateauxData] : null;
  const chateau = dbChateau || fallbackChateau;

  // Track page view for Google Ads
  React.useEffect(() => {
    if (chateau && id) {
      // Track page view
      gtag('config', 'G-WE28NLF8ZP', {
        page_title: `${chateau.nom} - Détail`,
        page_location: window.location.href,
        page_path: `/chateau/${id}`
      });

      // Track product view for Google Ads
      gtag('event', 'view_item', {
        currency: 'EUR',
        value: parseInt(chateau.prix) || 60,
        items: [{
          item_id: id,
          item_name: chateau.nom,
          item_category: 'Château Gonflable',
          price: parseInt(chateau.prix) || 60,
          quantity: 1
        }]
      });

      // Track dynamic conversion for page view
      gtag('event', 'conversion', {
        send_to: 'AW-1049756763/chateau_detail_view',
        value: parseInt(chateau.prix) || 60,
        currency: 'EUR',
        event_category: 'chateau',
        event_label: chateau.nom
      });
    }
  }, [chateau, id]);
  
  
  // Helper function pour accéder aux propriétés selon le type
  const getChateauProperty = (prop: string) => {
    if (dbChateau) {
      return (dbChateau as any)[prop];
    }
    return (fallbackChateau as any)?.[prop];
  };
  
  // Utilise SEULEMENT les images de la base de données
  const displayImages = imageUrls;

  const handleSaveChateau = async (updatedChateau: any) => {
    if (id && dbChateau) {
      try {
        await updateChateau(id, updatedChateau);
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error);
      }
    }
  };

  const handleDeleteChateau = async () => {
    if (id && dbChateau) {
      try {
        await deleteChateau(id);
        // Rediriger vers la liste des châteaux après suppression
        window.location.href = '/chateaux';
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  const handleToggleVisibility = async () => {
    if (id && dbChateau) {
      try {
        await toggleVisibility(id, !dbChateau.is_visible);
      } catch (error) {
        console.error('Erreur lors du changement de visibilité:', error);
      }
    }
  };

  if (!chateau) {
    return (
      <div className="min-h-screen bg-gradient-sky flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Château non trouvé</h1>
          <Link to="/chateaux">
            <Button variant="hero">Retour aux châteaux</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${chateau.nom} - Location Château Gonflable | Château de Clémentine`}
        description={`${chateau.description} Taille: ${chateau.taille}, Capacité: ${chateau.capacite}, Prix: ${chateau.prix} pour le week-end complet à Rhode-Saint-Genèse.`}
        keywords={`${chateau.nom}, château gonflable, location château, Rhode-Saint-Genèse, ${getChateauProperty('age_recommande') || getChateauProperty('ageRecommande')}, ${chateau.prix}`}
        canonicalUrl={`https://chateaudeclementine.com/chateau/${id}`}
      />
      <Header />
      <div className="min-h-screen bg-gradient-sky">
        <div className="container mx-auto px-4 py-8">
        {/* Navigation */}
        <div className="mb-8">
          <Link to="/chateaux">
            <Button variant="outline" className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour aux châteaux
            </Button>
          </Link>
        </div>

        {/* Admin Editor with Upload */}
        {isAdminLoggedIn && (
          <ChateauEditorWithUpload
            chateau={chateau}
            chateauId={id!}
            onSave={handleSaveChateau}
            onDelete={handleDeleteChateau}
            onToggleVisibility={handleToggleVisibility}
            isHidden={dbChateau ? !dbChateau.is_visible : false}
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images Carousel */}
          <div className="space-y-4">
            <div className="relative">
              {displayImages.length > 0 ? (
                <Carousel className="w-full">
                  <CarouselContent>
                    {displayImages.map((image, index) => (
                      <CarouselItem key={index}>
                        <div className="relative h-96 rounded-2xl overflow-hidden">
                          <img 
                            src={image}
                            alt={`${chateau.nom} - Image ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src = '/placeholder.svg';
                            }}
                          />
                          {index === 0 && (
                            <div className="absolute top-4 right-4">
                              <Badge variant="secondary" className="bg-white/95 text-primary font-bold text-xl px-4 py-2">
                                {chateau.prix}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious />
                  <CarouselNext />
                </Carousel>
              ) : (
                <div className="h-96 rounded-2xl overflow-hidden bg-muted flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-full h-full bg-muted animate-pulse flex items-center justify-center">
                      {isLoading ? (
                        <span className="text-muted-foreground">Chargement des images...</span>
                      ) : (
                        <div>
                          <span className="text-muted-foreground block mb-2">Aucune image disponible</span>
                          <Badge variant="secondary" className="bg-white/95 text-primary font-bold text-xl px-4 py-2">
                            {chateau.prix}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Galerie miniatures */}
            {displayImages.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {displayImages.map((image, index) => (
                  <div
                    key={index}
                    className="aspect-square rounded-lg overflow-hidden border border-border hover:border-primary/50 transition-all"
                  >
                    <img 
                      src={image}
                      alt={`${chateau.nom} - Miniature ${index + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                      onError={(e) => {
                        e.currentTarget.src = '/placeholder.svg';
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Informations */}
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl font-display font-bold mb-4">
                               <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  {dbChateau ? chateau.nom : chateau.nom}
                </span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {dbChateau ? chateau.description : chateau.description}
              </p>
            </div>

            {/* Informations principales */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="p-4 text-center">
                <Ruler className="w-6 h-6 mx-auto mb-2 text-primary" />
                <div className="text-sm font-medium text-muted-foreground">Taille</div>
                <div className="font-semibold">{dbChateau ? chateau.taille : chateau.taille}</div>
              </Card>
              <Card className="p-4 text-center">
                <Users className="w-6 h-6 mx-auto mb-2 text-secondary" />
                <div className="text-sm font-medium text-muted-foreground">Capacité</div>
                <div className="font-semibold">{dbChateau ? chateau.capacite : chateau.capacite}</div>
              </Card>
            </div>

            {/* Détails */}
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Âge recommandé</h3>
                <Badge variant="outline">{getChateauProperty('age_recommande') || getChateauProperty('ageRecommande')}</Badge>
              </div>

              <div>
                <h3 className="font-semibold mb-3">Caractéristiques</h3>
                <div className="grid grid-cols-2 gap-2">
                  {(dbChateau ? chateau.caracteristiques : chateau.caracteristiques).map((carac, index) => (
                    <div key={index} className="flex items-center">
                      <Star className="w-4 h-4 mr-2 text-accent" />
                      <span className="text-sm">{carac}</span>
                    </div>
                  ))}
                </div>
              </div>

              {!dbChateau && (chateau as any).inclus && (
                <div>
                  <h3 className="font-semibold mb-3">Inclus dans la location</h3>
                  <div className="space-y-2">
                    {(chateau as any).inclus.map((item: string, index: number) => (
                      <div key={index} className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 text-primary" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Informations supplémentaires - seulement pour châteaux hardcodés */}
            {!dbChateau && (chateau as any).espaceRequis && (
              <div className="grid grid-cols-2 gap-4 mb-6">
                <Card className="p-4 text-center">
                  <Calendar className="w-6 h-6 mx-auto mb-2 text-fun" />
                  <div className="text-sm font-medium text-muted-foreground">Espace requis</div>
                  <div className="font-semibold">{(chateau as any).espaceRequis}</div>
                </Card>
                <Card className="p-4 text-center">
                  <CheckCircle className="w-6 h-6 mx-auto mb-2 text-accent" />
                  <div className="text-sm font-medium text-muted-foreground">Installation</div>
                  <div className="font-semibold">{(chateau as any).installation}</div>
                </Card>
              </div>
            )}
          </div>
        </div>

        {/* Formulaire de réservation */}
        <div className="mt-16">
          <Card className="shadow-card border-0">
            <CardHeader>
              <CardTitle className="text-2xl font-display">Demande de Réservation</CardTitle>
              <p className="text-muted-foreground">
                Réservez ce château directement en remplissant le formulaire ci-dessous.
              </p>
            </CardHeader>
            <CardContent>
              <ReservationForm preSelectedChateau={id} />
            </CardContent>
          </Card>
        </div>
        </div>
      </div>
      <Footer />
    </>
  );
};